import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './shared/Icon';

// Main category definitions with subcategories. Key stems + non-translatable
// icon names — labels live in src/locales/en/search.json under
// categorySelector.categoryTree.<value>.(subcategories.<value>.(subSubcategories.<value>.))label.
const categoryTree = [
  {
    value: 'restaurants',
    icon: 'forkKnife',
    subcategories: [
      { value: 'italian', icon: 'forkKnife' },
      { value: 'asian', icon: 'forkKnife',
        subSubcategories: [
          { value: 'chinese', icon: 'forkKnife' },
          { value: 'japanese', icon: 'forkKnife' },
          { value: 'korean', icon: 'forkKnife' },
          { value: 'vietnamese', icon: 'forkKnife' },
          { value: 'indian', icon: 'forkKnife' },
          { value: 'thai', icon: 'forkKnife' }
        ]
      },
      { value: 'american', icon: 'forkKnife' },
      { value: 'mexican', icon: 'forkKnife' },
      { value: 'french', icon: 'forkKnife' },
      { value: 'seafood', icon: 'forkKnife' },
      { value: 'cafes', icon: 'coffee' },
      { value: 'desserts', icon: 'sparkles' },
      { value: 'fast_food', icon: 'car' }
    ]
  },
  {
    value: 'entertainment',
    icon: 'theater',
    subcategories: [
      { value: 'movies', icon: 'film' },
      { value: 'gaming', icon: 'sparkles' },
      { value: 'sports', icon: 'trophy' },
      { value: 'music', icon: 'musicalNote' },
      { value: 'venues', icon: 'theater' },
      { value: 'family', icon: 'userGroup' }
    ]
  },
  {
    value: 'bars',
    icon: 'beer',
    subcategories: [
      { value: 'cocktails', icon: 'cocktail' },
      { value: 'breweries', icon: 'beer' },
      { value: 'wine', icon: 'wine' },
      { value: 'nightlife', icon: 'moon' },
      { value: 'entertainment', icon: 'microphone' }
    ]
  },
  {
    value: 'parks',
    icon: 'tree',
    subcategories: [
      { value: 'nature', icon: 'tree' },
      { value: 'playgrounds', icon: 'stroller' },
      { value: 'hiking', icon: 'walking' },
      { value: 'sports', icon: 'trophy' },
      { value: 'water', icon: 'swimmer' },
      { value: 'winter', icon: 'snowflake' },
      { value: 'adventure', icon: 'mountain' },
      { value: 'camping', icon: 'backpack' },
      { value: 'golf', icon: 'trophy' }
    ]
  },
  {
    value: 'museums',
    icon: 'building',
    subcategories: [
      { value: 'art', icon: 'paintBrush' },
      { value: 'history', icon: 'bookOpen' },
      { value: 'performances', icon: 'building' }
    ]
  },
  {
    value: 'shopping',
    icon: 'shoppingBag',
    subcategories: [
      { value: 'clothing', icon: 'shoppingBag' },
      { value: 'electronics', icon: 'phone' },
      { value: 'home', icon: 'home' },
      { value: 'books', icon: 'bookOpen' },
      { value: 'jewelry', icon: 'diamond' },
      { value: 'malls', icon: 'shoppingCart' },
      { value: 'specialty', icon: 'paintBrush' }
    ]
  },
  {
    value: 'attractions',
    icon: 'mapPin',
    subcategories: [
      { value: 'landmarks', icon: 'building' },
      { value: 'monuments', icon: 'building' },
      { value: 'viewpoints', icon: 'eye' },
      { value: 'tourist', icon: 'star' }
    ]
  },
  {
    value: 'wellness',
    icon: 'spa',
    subcategories: [
      { value: 'fitness', icon: 'fire' },
      { value: 'spa', icon: 'spa' },
      { value: 'beauty', icon: 'sparkles' }
    ]
  },
  {
    value: 'educational',
    icon: 'bookOpen',
    subcategories: [
      { value: 'museums', icon: 'building' },
      { value: 'tours', icon: 'userGroup' }
    ]
  }
];

function ExpandableCategorySelector({ selectedItems = [], onChange }) {
  const { t } = useTranslation('search');
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [expandedSubcategories, setExpandedSubcategories] = useState(new Set());

  // Resolve the translated label for a category/subcategory/subSubcategory
  // triple. Keeps the path building in one place since it's used both when
  // rendering the tree and when resolving the single-selection display text.
  const getItemLabel = (categoryValue, subcategoryValue, subSubcategoryValue) => {
    if (subSubcategoryValue) {
      return t(`categorySelector.categoryTree.${categoryValue}.subcategories.${subcategoryValue}.subSubcategories.${subSubcategoryValue}.label`);
    }
    if (subcategoryValue) {
      return t(`categorySelector.categoryTree.${categoryValue}.subcategories.${subcategoryValue}.label`);
    }
    return t(`categorySelector.categoryTree.${categoryValue}.label`);
  };

  const toggleExpansion = (categoryValue, isSubcategory = false) => {
    const targetSet = isSubcategory ? expandedSubcategories : expandedCategories;
    const newSet = new Set(targetSet);
    
    if (newSet.has(categoryValue)) {
      newSet.delete(categoryValue);
    } else {
      newSet.add(categoryValue);
    }
    
    if (isSubcategory) {
      setExpandedSubcategories(newSet);
    } else {
      setExpandedCategories(newSet);
    }
  };

  const handleItemToggle = (categoryValue, subcategoryValue = null, subSubcategoryValue = null) => {
    // Create selection object
    const selection = {
      category: categoryValue,
      subcategory: subcategoryValue,
      subSubcategory: subSubcategoryValue
    };

    // Create unique key for the selection (for debugging)
    // const selectionKey = subSubcategoryValue 
    //   ? `${categoryValue}.${subcategoryValue}.${subSubcategoryValue}`
    //   : subcategoryValue 
    //     ? `${categoryValue}.${subcategoryValue}`
    //     : categoryValue;

    // Check if this selection already exists
    const existingIndex = selectedItems.findIndex(item => {
      if (subSubcategoryValue) {
        return item.category === categoryValue && 
               item.subcategory === subcategoryValue && 
               item.subSubcategory === subSubcategoryValue;
      } else if (subcategoryValue) {
        return item.category === categoryValue && 
               item.subcategory === subcategoryValue && 
               !item.subSubcategory;
      } else {
        return item.category === categoryValue && 
               !item.subcategory && 
               !item.subSubcategory;
      }
    });

    let newSelectedItems;
    if (existingIndex >= 0) {
      // Remove existing selection
      newSelectedItems = selectedItems.filter((_, index) => index !== existingIndex);
    } else {
      // Add new selection
      newSelectedItems = [...selectedItems, selection];
    }

    onChange(newSelectedItems);
  };

  const handleSelectAll = () => {
    // If all main categories are selected, deselect all. Otherwise, select all main categories
    const allMainCategoriesSelected = categoryTree.every(cat => 
      selectedItems.some(item => item.category === cat.value && !item.subcategory)
    );

    if (allMainCategoriesSelected) {
      onChange([]);
    } else {
      const allMainCategories = categoryTree.map(cat => ({
        category: cat.value,
        subcategory: null,
        subSubcategory: null
      }));
      onChange(allMainCategories);
    }
  };

  const isItemSelected = (categoryValue, subcategoryValue = null, subSubcategoryValue = null) => {
    return selectedItems.some(item => {
      if (subSubcategoryValue) {
        return item.category === categoryValue && 
               item.subcategory === subcategoryValue && 
               item.subSubcategory === subSubcategoryValue;
      } else if (subcategoryValue) {
        return item.category === categoryValue && 
               item.subcategory === subcategoryValue && 
               !item.subSubcategory;
      } else {
        return item.category === categoryValue && 
               !item.subcategory && 
               !item.subSubcategory;
      }
    });
  };

  const getDisplayText = () => {
    if (selectedItems.length === 0) {
      return t('categorySelector.displayText.placeholder');
    }

    const totalPossibleSelections = categoryTree.length; // Just counting main categories for simplicity
    if (selectedItems.length === totalPossibleSelections) {
      return t('categorySelector.displayText.allSelected');
    }

    if (selectedItems.length === 1) {
      const item = selectedItems[0];
      const category = categoryTree.find(cat => cat.value === item.category);

      let foundItem = null;
      if (item.subSubcategory) {
        const subcategory = category?.subcategories?.find(sub => sub.value === item.subcategory);
        foundItem = subcategory?.subSubcategories?.find(subsub => subsub.value === item.subSubcategory);
      } else if (item.subcategory) {
        foundItem = category?.subcategories?.find(sub => sub.value === item.subcategory);
      } else {
        foundItem = category;
      }

      if (foundItem) {
        const label = getItemLabel(item.category, item.subcategory, item.subSubcategory);
        return <span className="flex items-center gap-2">{foundItem.icon && <Icon name={foundItem.icon} className="w-4 h-4" />} {label}</span>;
      }
      return item.category;
    }

    return t('categorySelector.displayText.itemsSelectedCount', { count: selectedItems.length });
  };

  const allSelected = categoryTree.every(cat => 
    selectedItems.some(item => item.category === cat.value && !item.subcategory)
  );
  const someSelected = selectedItems.length > 0 && !allSelected;

  return (
    <div className="relative">
      {/* Display selected items */}
      <div 
        className="w-full p-2 border border-border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 bg-surface cursor-pointer flex items-center justify-between transition-all hover:border-border"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-content-primary">{getDisplayText()}</span>
        <Icon name="chevronDown" className={`w-4 h-4 text-content-muted transition-transform duration-normal ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {/* Expandable dropdown */}
      {isOpen && (
        <div className="absolute top-full mt-1 min-w-[260px] w-max bg-surface border border-border rounded-lg shadow-elevation-3 z-modal max-h-80 overflow-y-auto">
          {/* Select All option */}
          <div className="sticky top-0 bg-surface border-b border-border">
            <label className="flex items-center px-3 py-1.5 hover:bg-background cursor-pointer">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={handleSelectAll}
                className="mr-2 h-3.5 w-3.5 text-primary rounded-lg focus-visible:ring-primary"
                ref={input => {
                  if (input) {
                    input.indeterminate = someSelected;
                  }
                }}
              />
              <span className="font-medium text-sm text-content-primary">
                {allSelected ? t('categorySelector.selectAllOption.deselectAll') : t('categorySelector.selectAllOption.selectAll')}
              </span>
            </label>
          </div>

          {/* Category tree */}
          {categoryTree.map((category) => (
            <div key={category.value} className="border-b border-border last:border-b-0">
              {/* Main category */}
              <div className="flex items-center">
                <label className="flex items-center px-3 py-1.5 hover:bg-background cursor-pointer flex-grow">
                  <input
                    type="checkbox"
                    checked={isItemSelected(category.value)}
                    onChange={() => handleItemToggle(category.value)}
                    className="mr-2 h-3.5 w-3.5 text-primary rounded-lg focus-visible:ring-primary"
                  />
                  <span className="flex items-center gap-1.5">
                    <Icon name={category.icon} className="w-4 h-4" />
                    <span className="text-sm text-content-primary">{getItemLabel(category.value)}</span>
                  </span>
                </label>

                {/* Expand button for categories with subcategories */}
                {category.subcategories && (
                  <button
                    onClick={() => toggleExpansion(category.value)}
                    className="px-2 py-1.5 hover:bg-background transition-colors"
                    aria-label={expandedCategories.has(category.value)
                      ? t('categorySelector.categoryExpandToggle.collapseAriaLabel', { label: getItemLabel(category.value) })
                      : t('categorySelector.categoryExpandToggle.expandAriaLabel', { label: getItemLabel(category.value) })}
                  >
                    <Icon name="chevronRight" className={`w-3.5 h-3.5 text-content-muted transition-transform duration-normal ${
                      expandedCategories.has(category.value) ? 'rotate-90' : ''
                    }`} />
                  </button>
                )}
              </div>

              {/* Subcategories */}
              {category.subcategories && expandedCategories.has(category.value) && (
                <div className="bg-background">
                  {category.subcategories.map((subcategory) => (
                    <div key={subcategory.value}>
                      {/* Subcategory */}
                      <div className="flex items-center">
                        <label className="flex items-center px-6 py-1 hover:bg-surface-secondary cursor-pointer flex-grow">
                          <input
                            type="checkbox"
                            checked={isItemSelected(category.value, subcategory.value)}
                            onChange={() => handleItemToggle(category.value, subcategory.value)}
                            className="mr-2 h-3 w-3 text-primary rounded-lg focus-visible:ring-primary"
                          />
                          <span className="text-content-secondary text-xs flex items-center gap-1">{subcategory.icon && <Icon name={subcategory.icon} className="w-3.5 h-3.5" />} {getItemLabel(category.value, subcategory.value)}</span>
                        </label>

                        {/* Expand button for subcategories with sub-subcategories */}
                        {subcategory.subSubcategories && (
                          <button
                            onClick={() => toggleExpansion(`${category.value}.${subcategory.value}`, true)}
                            className="px-2 py-1 hover:bg-background transition-colors"
                            aria-label={expandedSubcategories.has(`${category.value}.${subcategory.value}`)
                              ? t('categorySelector.subcategoryExpandToggle.collapseAriaLabel', { label: getItemLabel(category.value, subcategory.value) })
                              : t('categorySelector.subcategoryExpandToggle.expandAriaLabel', { label: getItemLabel(category.value, subcategory.value) })}
                          >
                            <Icon name="chevronRight" className={`w-3 h-3 text-content-muted transition-transform duration-normal ${
                              expandedSubcategories.has(`${category.value}.${subcategory.value}`) ? 'rotate-90' : ''
                            }`} />
                          </button>
                        )}
                      </div>

                      {/* Sub-subcategories */}
                      {subcategory.subSubcategories && expandedSubcategories.has(`${category.value}.${subcategory.value}`) && (
                        <div className="bg-surface-secondary">
                          {subcategory.subSubcategories.map((subSubcategory) => (
                            <label
                              key={subSubcategory.value}
                              className="flex items-center px-9 py-1 hover:bg-background cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={isItemSelected(category.value, subcategory.value, subSubcategory.value)}
                                onChange={() => handleItemToggle(category.value, subcategory.value, subSubcategory.value)}
                                className="mr-2 h-3 w-3 text-primary rounded-lg focus-visible:ring-primary"
                              />
                              <span className="text-content-secondary text-xs flex items-center gap-1">{subSubcategory.icon && <Icon name={subSubcategory.icon} className="w-3 h-3" />} {getItemLabel(category.value, subcategory.value, subSubcategory.value)}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Action buttons */}
          <div className="sticky bottom-0 bg-background px-3 py-2 border-t border-border flex justify-between items-center">
            <span className="text-xs text-content-secondary">
              {t('categorySelector.footer.selectedCount', { count: selectedItems.length })}
            </span>
            <button
              onClick={() => setIsOpen(false)}
              className="px-3 py-1.5 bg-primary text-content-inverse rounded-lg hover:bg-primary-dark hover:shadow-card-hover active:scale-[0.98] transition-all duration-fast text-xs font-medium"
            >
              {t('categorySelector.footer.doneButton')}
            </button>
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-overlay" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}

export default ExpandableCategorySelector;